import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { peppolLookup } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { CopyableText } from '../../components/CopyableText';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { formatDate } from '../../utils/format';
import type { FlowisPeppolParticipant } from '../../api/types';
import {
  defaultSchemeCode,
  documentTypeLabel,
  parseParticipantInput,
  participantSchemes,
} from './participantSchemes';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
    </Box>
  );
}

function ParticipantCard({ participant }: { participant: FlowisPeppolParticipant }) {
  const { t } = useTranslation();

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">{participant.name ?? t('peppol.nameMissing')}</Typography>

        <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
          <Field label={t('peppol.participantId')}>
            <CopyableText value={participant.participantId} />
          </Field>
          {participant.countryCode && <Field label={t('columns.country')}>{participant.countryCode}</Field>}
          {participant.registrationDate && (
            <Field label={t('peppol.registered')}>{formatDate(participant.registrationDate)}</Field>
          )}
        </Stack>

        {participant.documentTypes.length > 0 && (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              {t('peppol.documentTypes')}
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {participant.documentTypes.map((documentType) => (
                <Tooltip key={documentType} title={documentType}>
                  <Chip size="small" variant="outlined" label={documentTypeLabel(documentType)} />
                </Tooltip>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}

/** The `scheme:value` form the directory is asked for; empty when there is no value yet. */
function toParticipantId(schemeCode: string, text: string): string {
  const compact = text.replace(/\s+/g, '');
  return compact.length > 0 ? `${schemeCode}:${compact}` : '';
}

export function PeppolCheckPage() {
  const { t } = useTranslation();

  const [schemeCode, setSchemeCode] = useState<string>(defaultSchemeCode);
  const [value, setValue] = useState('');
  /** The identifier a lookup was requested for; null until the first submit. */
  const [submitted, setSubmitted] = useState<string | null>(null);

  const scheme = participantSchemes.find((entry) => entry.code === schemeCode);
  const participantId = toParticipantId(schemeCode, value);

  /**
   * Applies pasted or completed input: a recognised prefix moves into the dropdown. Returns the
   * identifier that results, because state set here is not visible until the next render — Enter
   * has to look up what the parse produced, not what the field showed before it.
   */
  const applyParsed = (text: string): string => {
    const parsed = parseParticipantInput(text);
    if (parsed.schemeCode) setSchemeCode(parsed.schemeCode);
    setValue(parsed.value);

    return toParticipantId(parsed.schemeCode ?? schemeCode, parsed.value);
  };

  const query = useQuery({
    queryKey: queryKeys.flowis.peppolLookup(submitted ?? ''),
    queryFn: ({ signal }) => peppolLookup(submitted ?? '', signal),
    enabled: submitted !== null,
    // The directory is public and registrations move slowly; keep the answer for the session.
    staleTime: 5 * 60_000,
  });

  const lookUp = (id: string = participantId) => {
    if (id.length > 0) setSubmitted(id);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">{t('peppol.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('peppol.description')}
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
            <TextField
              select
              size="small"
              label={t('peppol.scheme')}
              value={schemeCode}
              onChange={(event) => setSchemeCode(event.target.value)}
              sx={{ minWidth: 320 }}
              slotProps={{
                select: {
                  // The closed control shows just the essentials; the open list carries the detail.
                  renderValue: (selected) => {
                    const entry = participantSchemes.find((item) => item.code === selected);
                    return entry ? `${entry.code} — ${t(entry.labelKey)}` : String(selected);
                  },
                },
              }}
            >
              {participantSchemes.map((entry) => (
                <MenuItem key={entry.code} value={entry.code}>
                  <ListItemText
                    primary={`${entry.code} — ${t(entry.labelKey)}`}
                    secondary={t('peppol.example', { value: entry.example })}
                  />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label={t('peppol.identifier')}
              value={value}
              onChange={(event) => {
                const text = event.target.value;
                // A typed `scheme:` form splits the moment it is complete; anything else stays as is
                // until paste or blur, so keystrokes are never rewritten mid-word.
                if (/^\s*\d{4}:.+/.test(text)) {
                  applyParsed(text);
                } else {
                  setValue(text);
                }
              }}
              onPaste={(event) => {
                event.preventDefault();
                applyParsed(event.clipboardData.getData('text'));
              }}
              onBlur={() => {
                if (value.trim().length > 0) applyParsed(value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') lookUp(applyParsed(value));
              }}
              helperText={t('peppol.identifierHelp')}
              sx={{ minWidth: 280, flexGrow: 1 }}
            />

            <Button
              variant="contained"
              startIcon={<TravelExploreIcon />}
              disabled={participantId.length === 0 || (submitted === participantId && query.isFetching)}
              onClick={() => lookUp()}
            >
              {t('peppol.lookUp')}
            </Button>
          </Stack>

          {participantId.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('peppol.sentAs')}{' '}
              <Box component="code" sx={{ fontFamily: 'monospace' }}>
                {participantId}
              </Box>
              {scheme && ` · ${t('peppol.example', { value: `${scheme.code}:${scheme.example}` })}`}
            </Typography>
          )}
        </Stack>
      </Paper>

      {submitted !== null && (
        <>
          {query.isPending && <LoadingBlock label={t('peppol.checking')} />}
          {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

          {query.isSuccess &&
            (query.data.length === 0 ? (
              <Alert severity="warning">{t('peppol.notFound')}</Alert>
            ) : (
              <>
                <Alert severity="success">{t('peppol.found')}</Alert>
                {query.data.map((participant) => (
                  <ParticipantCard key={participant.participantId} participant={participant} />
                ))}
              </>
            ))}
        </>
      )}
    </Stack>
  );
}
